import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { RootState } from "app/store";
import { getAuthTokenWithRefresh } from "features/auth/utils";
import { setSessionExpired } from "features/auth/slice";

/**
 * Higher-order function that wraps a base query with automatic token refresh on 401.
 * On 401: attempts a single token refresh (shared via singleton lock).
 * If refresh succeeds, retries the request with tokenOverride. If it fails,
 * sets sessionExpired in Redux and short-circuits all future requests.
 */
export const withReauth = (
  baseQueryFn: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> => {
  return async (args, api, extraOptions) => {
    const { sessionExpired } = (api.getState() as RootState).auth;
    if (sessionExpired) {
      return {
        error: { status: 401, data: { message: "Session expired" } },
      };
    }

    let result = await baseQueryFn(args, api, extraOptions);

    if (result.error?.status === 401) {
      // Attempt one token refresh (singleton lock prevents parallel refreshes)
      const newToken = await getAuthTokenWithRefresh();
      if (newToken) {
        // Retry with fresh token
        result = await baseQueryFn(args, api, {
          ...extraOptions,
          tokenOverride: newToken,
        });
        if (result.error?.status === 401) {
          // Token refreshed but still rejected — stop the cycle
          api.dispatch(setSessionExpired());
        }
      } else {
        // Refresh token is permanently expired
        api.dispatch(setSessionExpired());
      }
    }

    return result;
  };
};
